(() => {
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const define = (name, component) => { if (!customElements.get(name)) customElements.define(name, component); };
  class ContentElement extends HTMLElement {
    listen(node, event, callback, options = {}) { node?.addEventListener(event, callback, { ...options, signal: this.events.signal }); }
    start() { this.events?.abort(); if (this.closest('[data-marquee-clone]')) return false; this.events = new AbortController(); return true; }
    disconnectedCallback() { this.events?.abort(); }
  }
  define('studio-content-slider', class extends ContentElement {
    connectedCallback() {
      if (!this.start()) return; this.viewport = this.querySelector('[data-st-viewport]');
      if (!this.viewport) return;
      this.items = [...this.viewport.children]; this.paused = this.querySelector('[data-st-pause]')?.getAttribute('aria-pressed') === 'true'; this.hovered = false; this.focused = this.contains(document.activeElement);
      this.rtl = getComputedStyle(this).direction === 'rtl';
      this.centered = this.dataset.center === 'true';
      this.listen(this.querySelector('[data-st-prev]'), 'click', () => this.move(-1));
      this.listen(this.querySelector('[data-st-next]'), 'click', () => this.move(1));
      this.listen(this.querySelector('[data-st-pause]'), 'click', (event) => {
        this.paused = !this.paused; event.currentTarget.setAttribute('aria-pressed', String(this.paused));
        this.clock();
      });
      this.listen(this.viewport, 'scroll', () => this.update(), { passive: true });
      this.items.forEach((item, index) => {
        this.listen(item, 'pointerenter', () => { if (this.dataset.hover === 'hover') this.select(index); });
        this.listen(item, 'focusin', () => this.select(index));
      });
      this.listen(document, 'shopify:block:select', (event) => { const index = this.items.findIndex(item => item === event.target || item.contains(event.target)); if (index >= 0) this.go(index); });
      this.listen(this, 'pointerenter', () => { this.hovered = true; this.clock(); });
      this.listen(this, 'pointerleave', () => { this.hovered = false; this.clock(); });
      this.listen(this, 'focusin', () => { this.focused = true; this.clock(); });
      this.listen(this, 'focusout', (event) => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.clock(); } });
      this.listen(document, 'visibilitychange', () => this.clock());
      this.listen(motion, 'change', () => this.clock());
      this.resize = new ResizeObserver(() => this.update()); this.resize.observe(this.viewport);
      this.setAttribute('data-ready', ''); this.update(); this.clock();
    }
    position() {
      const viewport = this.viewport.getBoundingClientRect();
      const start = this.centered ? viewport.left + viewport.width / 2 : this.rtl ? viewport.right : viewport.left;
      let best = 0; let closest = Infinity;
      this.items.forEach((item, index) => { const bounds = item.getBoundingClientRect(); const edge = this.centered ? bounds.left + bounds.width / 2 : this.rtl ? bounds.right : bounds.left; const distance = Math.abs(edge - start); if (distance < closest) { closest = distance; best = index; } });
      return best;
    }
    select(index) { this.items.forEach((item, i) => item.classList.toggle('st-active', i === index)); }
    go(index) {
      const destination = this.items[index]; if (!destination) return;
      const box = destination.getBoundingClientRect(); const viewport = this.viewport.getBoundingClientRect();
      const left = this.centered ? box.left - viewport.left + (box.width - viewport.width) / 2 : this.rtl ? box.right - viewport.right : box.left - viewport.left;
      this.viewport.scrollBy({ left, behavior: motion.matches ? 'instant' : 'smooth' });
      this.select(index);
    }
    move(step) {
      const index = this.position(); const next = Math.max(0, Math.min(this.items.length - 1, index + step));
      this.go(next);
    }
    update() {
      const index = this.position(); const hasOverflow = this.viewport.scrollWidth > this.viewport.clientWidth + 2;
      const last = this.items.at(-1); const bounds = this.viewport.getBoundingClientRect();
      const endVisible = !last || (this.rtl ? last.getBoundingClientRect().left >= bounds.left - 3 : last.getBoundingClientRect().right <= bounds.right + 3);
      const previous = this.querySelector('[data-st-prev]'); const next = this.querySelector('[data-st-next]');
      if (previous) previous.disabled = index === 0 || !hasOverflow;
      if (next) next.disabled = endVisible || !hasOverflow;
      const counter = this.querySelector('[data-st-count]'); if (counter) counter.textContent = hasOverflow ? `${index + 1} / ${this.items.length}` : '';
      const progress = this.querySelector('.st-slider-progress');
      if (progress) {
        const maximum = this.viewport.scrollWidth - this.viewport.clientWidth;
        const value = maximum > 0 ? Math.min(100, Math.round(Math.abs(this.viewport.scrollLeft) / maximum * 100)) : 100;
        progress.setAttribute('aria-valuenow', String(value)); progress.style.setProperty('--st-progress', `${value}%`);
      }
      this.atEnd = endVisible; this.select(index);
    }
    clock() {
      clearInterval(this.timer);
      if (this.dataset.autoplay !== 'true' || motion.matches || this.paused || this.hovered || this.focused || document.hidden || this.items.length < 2) return;
      this.timer = setInterval(() => {
        if (this.atEnd || this.position() >= this.items.length - 1) this.go(0); else this.move(1);
      }, Math.max(3, Number(this.dataset.delay) || 5) * 1000);
    }
    disconnectedCallback() { super.disconnectedCallback(); clearInterval(this.timer); this.resize?.disconnect(); }
  });
  define('studio-content-tabs', class extends ContentElement {
    connectedCallback() {
      if (!this.start()) return;
      this.tabs = [...this.querySelectorAll('[role=tab]')].filter(node => node.closest('studio-content-tabs') === this);
      this.panels = [...this.querySelectorAll('[role=tabpanel]')].filter(node => node.closest('studio-content-tabs') === this);
      this.tabs.forEach((tab, index) => {
        this.listen(tab, 'click', (event) => { event.preventDefault(); this.select(index); });
        this.listen(tab, 'keydown', (event) => {
          const rtl = getComputedStyle(this).direction === 'rtl'; const directions = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 };
          let next;
          if (event.key in directions) next = (index + directions[event.key] + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); this.select(next); this.tabs[next].focus(); }
        });
      });
      this.listen(document, 'shopify:block:select', (event) => { const panel = event.target.closest('[role=tabpanel]'); const index = this.panels.indexOf(panel); if (index >= 0) this.select(index); });
      this.setAttribute('data-ready', ''); this.select(Math.max(0, this.tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true')));
    }
    select(index) { this.tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; }); this.panels.forEach((panel, i) => { panel.hidden = i !== index; }); }
  });
  define('studio-image-compare', class extends ContentElement {
    connectedCallback() { if (!this.start()) return; const slider = this.querySelector('input[type=range]'); if (!slider) return; const update = () => { this.style.setProperty('--st-compare-position', `${slider.value}%`); slider.setAttribute('aria-valuetext', `${slider.value}%`); }; this.listen(slider, 'input', update); update(); }
  });
  define('studio-content-countdown', class extends ContentElement {
    connectedCallback() {
      if (!this.start()) return; clearInterval(this.timer); this.end = this.deadline(); if (this.paint()) this.timer = setInterval(() => this.paint(), 1000);
    }
    deadline() {
      if (this.dataset.kind !== 'evergreen') {
        const value = this.dataset.until || '';
        const parts = value.trim().match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(?:\s*(AM|PM)|\s*(Z|[+-]\d{2}:\d{2}))?)?$/i);
        if (!parts) return NaN;
        const year = Number(parts[1]), month = Number(parts[2]) - 1, day = Number(parts[3]);
        let hour = Number(parts[4] || 0); const minute = Number(parts[5] || 0), second = Number(parts[6] || 0), millisecond = Number((parts[7] || '').padEnd(3, '0'));
        if (month < 0 || month > 11 || day < 1 || day > new Date(Date.UTC(year, month + 1, 0)).getUTCDate() || minute > 59 || second > 59 || (parts[8] ? hour < 1 || hour > 12 : hour > 23)) return NaN;
        if (parts[8]) hour = hour % 12 + (parts[8].toUpperCase() === 'PM' ? 12 : 0);
        if (parts[9]) {
          const zone = parts[9]; let offset = 0;
          if (zone.toUpperCase() !== 'Z') { const hours = Number(zone.slice(1, 3)), minutes = Number(zone.slice(4, 6)); if (hours > 23 || minutes > 59) return NaN; offset = (hours * 60 + minutes) * (zone[0] === '-' ? -1 : 1); }
          return Date.UTC(year, month, day, hour, minute, second, millisecond) - offset * 60000;
        }
        const date = new Date(year, month, day, hour, minute, second, millisecond);
        return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day && date.getHours() === hour ? date.getTime() : NaN;
      }
      const now = new Date(); const next = new Date(now); const duration = this.dataset.duration;
      if (duration === 'every_month') { next.setMonth(next.getMonth() + 1, 1); next.setHours(0, 0, 0, 0); }
      else if (duration === 'every_week') { next.setDate(next.getDate() + (7 - next.getDay() || 7)); next.setHours(0, 0, 0, 0); }
      else { const hours = Number(duration?.match(/\d+/)?.[0]) || 24; next.setHours(Math.floor(next.getHours() / hours) * hours + hours, 0, 0, 0); }
      return next.getTime();
    }
    paint() {
      let seconds = Math.max(0, Math.ceil((this.end - Date.now()) / 1000));
      if (!Number.isFinite(seconds)) { this.hidden = true; clearInterval(this.timer); return false; }
      this.hidden = false;
      if (seconds === 0 && this.dataset.kind === 'evergreen') { this.end = this.deadline(); seconds = Math.max(0, Math.ceil((this.end - Date.now()) / 1000)); }
      const units = { days: Math.floor(seconds / 86400), hours: Math.floor(seconds / 3600) % 24, minutes: Math.floor(seconds / 60) % 60, seconds: seconds % 60 };
      for (const [unit, value] of Object.entries(units)) { const output = this.querySelector(`[data-unit=${unit}]`); if (output) output.textContent = String(value).padStart(2, '0'); }
      this.toggleAttribute('data-expired', seconds === 0);
      if (seconds === 0 && this.dataset.kind !== 'evergreen') clearInterval(this.timer);
      return seconds > 0;
    }
    disconnectedCallback() { super.disconnectedCallback(); clearInterval(this.timer); }
  });
  define('studio-copy-code', class extends ContentElement {
    connectedCallback() { if (!this.start()) return; this.listen(this.querySelector('[data-copy]'), 'click', async (event) => {
      const button = event.currentTarget; const text = button.dataset.copy;
      try { await navigator.clipboard.writeText(text); button.textContent = button.dataset.copied; const status = this.querySelector('[role=status]'); if (status) status.textContent = button.dataset.copied; }
      catch { const code = this.querySelector('code'); const selection = getSelection(); if (!code || !selection) return; const range = document.createRange(); range.selectNodeContents(code); selection.removeAllRanges(); selection.addRange(range); }
    }); }
  });
  define('studio-content-popup', class extends ContentElement {
    connectedCallback() {
      if (!this.start()) return; this.dialog = this.querySelector('dialog'); if (!this.dialog) return;
      this.storageKey = `studio-popup-${this.dataset.key}`;
      this.listen(this.querySelector('[data-popup-open]'), 'click', () => this.open());
      this.listen(this.querySelector('[data-teaser-close]'), 'click', (event) => { const teaser = this.querySelector('[data-popup-open]'); if (teaser) teaser.hidden = true; event.currentTarget.hidden = true; });
      this.listen(this.querySelector('[data-popup-close]'), 'click', () => this.dialog.close());
      this.listen(this.dialog, 'click', (event) => { if (event.target !== this.dialog) return; const bounds = this.dialog.getBoundingClientRect(); if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close(); });
      this.listen(this.dialog, 'close', () => { this.remember(); this.opener?.focus(); });
      this.listen(document, 'shopify:section:select', (event) => { if (this.closest('.shopify-section') === event.target) this.open(); });
      if (this.dataset.editor === 'true' || !this.canShow()) return;
      const setup = () => {
        const trigger = this.dataset.trigger || '';
        const automatic = () => { if (!this.shown && this.canShow()) this.open(); };
        if (trigger === 'immediately' || trigger === 'scroll_down' && window.matchMedia('(max-width: 749px)').matches) this.timer = setTimeout(automatic, 0);
        else if (trigger === 'delay') this.timer = setTimeout(automatic, 5000);
        else if (trigger.includes('scroll')) this.listen(window, 'scroll', () => { if (scrollY >= Math.min(300, Math.max(80, innerHeight * .3))) automatic(); }, { passive: true });
        else if (trigger.includes('exit')) this.listen(document, 'mouseout', (event) => { if (event.clientY <= 0 && !event.relatedTarget) automatic(); });
      };
      if (document.readyState === 'loading') this.listen(window, 'load', setup, { once: true }); else setup();
    }
    remember() { if (this.dataset.editor !== 'true') { try { localStorage.setItem(this.storageKey, String(Date.now())); } catch {} } }
    canShow() {
      let last = 0; try { last = Number(localStorage.getItem(this.storageKey)) || 0; } catch {}
      const cadence = this.dataset.repeat;
      if (!last) return true;
      if (cadence === 'no_repeat' || cadence?.includes('once')) return false;
      if (cadence === 'every_month') { const next = new Date(last); next.setMonth(next.getMonth() + 1); return Date.now() >= next.getTime(); }
      const periods = { every_30_mins: 1800000, every_1_hr: 3600000, every_6_hrs: 21600000, every_12_hrs: 43200000, every_day: 86400000, every_3_days: 259200000, every_week: 604800000, every_2_weeks: 1209600000 };
      const duration = periods[cadence] ?? 86400000;
      return Date.now() - last >= duration;
    }
    open() { clearTimeout(this.timer); if (!this.dialog.open) { this.opener = document.activeElement; this.dialog.showModal(); this.shown = true; this.remember(); } }
    disconnectedCallback() { super.disconnectedCallback(); clearTimeout(this.timer); if (this.dialog?.open) this.dialog.close(); }
  });
  define('studio-content-marquee', class extends ContentElement {
    connectedCallback() {
      if (!this.start()) return; this.track = this.querySelector('.st-marquee-track'); if (!this.track) return;
      this.group = this.track.querySelector('.st-marquee-group'); if (!this.group) return;
      this.paused = this.querySelector('.st-marquee-pause')?.getAttribute('aria-pressed') === 'true'; this.hovered = false; this.focused = this.contains(document.activeElement);
      this.listen(this.querySelector('.st-marquee-pause'), 'click', (event) => { this.paused = !this.paused; event.currentTarget.setAttribute('aria-pressed', String(this.paused)); });
      this.listen(this, 'pointerenter', () => { this.hovered = this.dataset.pauseHover === 'true'; });
      this.listen(this, 'pointerleave', () => { this.hovered = false; });
      this.listen(this, 'focusin', () => { this.focused = true; });
      this.listen(this, 'focusout', (event) => { this.focused = this.contains(event.relatedTarget); });
      this.listen(motion, 'change', () => this.measure());
      this.resize = new ResizeObserver(() => this.measure()); this.resize.observe(this.group); this.resize.observe(this.track);
      this.measure();
      this.visible = false; this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; }); this.observer.observe(this);
      this.last = performance.now();
      const animate = (now) => {
        const delta = Math.min(50, now - this.last); this.last = now;
        if (!motion.matches && this.visible && !document.hidden && !this.paused && !this.hovered && !this.focused) {
          const amount = delta * (Number(this.dataset.speed) || .3) * .12;
          if (this.period > 0) {
            const direction = this.dataset.direction === 'right' ? -1 : 1;
            this.offset = ((this.offset + amount * direction) % this.period + this.period) % this.period;
            this.track.scrollLeft = this.offset;
          }
        }
        this.frame = requestAnimationFrame(animate);
      };
      this.frame = requestAnimationFrame(animate);
    }
    measure() {
      this.track.querySelectorAll('[data-marquee-clone]').forEach(node => node.remove());
      this.period = 0;
      if (motion.matches) { this.offset = 0; this.track.scrollLeft = 0; return; }
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const width = this.group.getBoundingClientRect().width + gap;
      if (width < 1) return;
      this.period = width; this.offset = (this.offset || 0) % width;
      const copies = Math.min(12, Math.max(1, Math.ceil(this.track.clientWidth / width) + 1));
      for (let i = 0; i < copies; i++) {
        const clone = this.group.cloneNode(true); clone.dataset.marqueeClone = ''; clone.setAttribute('aria-hidden', 'true'); clone.setAttribute('inert', '');
        for (const node of [clone, ...clone.querySelectorAll('*')]) { node.removeAttribute('id'); node.removeAttribute('data-shopify-editor-block'); if (node.matches('a,button,input,select,textarea,[tabindex]')) node.setAttribute('tabindex', '-1'); }
        this.track.append(clone);
      }
      this.track.scrollLeft = this.offset;
    }
    disconnectedCallback() { super.disconnectedCallback(); cancelAnimationFrame(this.frame); this.observer?.disconnect(); this.resize?.disconnect(); this.track?.querySelectorAll('[data-marquee-clone]').forEach(node => node.remove()); }
  });
  define('studio-content-video', class extends ContentElement {
    connectedCallback() {
      if (!this.start()) return; this.button = this.querySelector('.st-video-facade'); this.template = this.querySelector('template');
      this.listen(this.button, 'click', () => this.load(true));
      const userControl = (event) => { if (event.target.closest('video')) this.userStarted = true; };
      this.listen(this, 'pointerdown', userControl); this.listen(this, 'keydown', userControl);
      if (this.dataset.autoplay === 'true') {
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          if (this.userStarted) return;
          if (entry.isIntersecting && !motion.matches && !this.loaded) this.load(false);
          else this.playback(entry.isIntersecting && !motion.matches);
        }, { threshold: .25 }); this.observer.observe(this);
      }
      this.listen(motion, 'change', () => { if (!this.userStarted) { if (!motion.matches && this.visible && !this.loaded) this.load(false); else this.playback(!motion.matches && this.visible); } });
    }
    load(userStarted) {
      this.userStarted = userStarted || this.userStarted;
      if (!this.loaded && this.template) {
        const fragment = this.template.content.cloneNode(true); const iframe = fragment.querySelector('iframe');
        if (!iframe && !fragment.querySelector('video')) return;
        if (iframe) { const url = new URL(iframe.src); url.searchParams.set('autoplay', '1'); if (!userStarted || this.dataset.muted === 'true') { url.searchParams.set('mute', '1'); url.searchParams.set('muted', '1'); } iframe.src = url.href; }
        this.append(fragment); this.loaded = true; if (this.button) this.button.hidden = true; this.setAttribute('data-loaded', '');
      }
      const video = this.querySelector('video'); if (video) { if (!userStarted) video.muted = true; video.play().catch(() => {}); if (userStarted) video.focus(); }
      else if (userStarted) this.querySelector('iframe')?.focus();
    }
    playback(playing) {
      const video = this.querySelector('video'); if (video) { if (playing) video.play().catch(() => {}); else video.pause(); }
      const iframe = this.querySelector('iframe');
      if (iframe?.contentWindow) {
        const origin = new URL(iframe.src).origin;
        if (origin === 'https://www.youtube-nocookie.com') iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: playing ? 'playVideo' : 'pauseVideo', args: [] }), origin);
        if (origin === 'https://player.vimeo.com') iframe.contentWindow.postMessage(JSON.stringify({ method: playing ? 'play' : 'pause' }), origin);
      }
    }
    disconnectedCallback() { super.disconnectedCallback(); this.observer?.disconnect(); this.playback(false); }
  });
  define('studio-content-expand', class extends ContentElement {
    connectedCallback() {
      if (!this.start()) return; this.content = this.querySelector('.st-expand-content'); this.button = this.querySelector('.st-expand-toggle'); if (!this.content || !this.button) return;
      this.expanded = false; this.limit = Math.max(0, Number(this.dataset.height) || 0);
      this.listen(this.button, 'click', () => { this.expanded = !this.expanded; this.refresh(); });
      this.listen(this.content, 'focusin', (event) => { if (event.target.getBoundingClientRect().bottom > this.content.getBoundingClientRect().bottom) { this.expanded = true; this.refresh(); } });
      this.resize = new ResizeObserver(() => this.refresh()); const child = this.content.firstElementChild; if (child) this.resize.observe(child);
      this.refresh();
    }
    refresh() {
      const overflowing = this.limit > 0 && this.content.scrollHeight > this.limit + 4;
      this.button.hidden = !overflowing; this.toggleAttribute('data-collapsed', overflowing && !this.expanded);
      this.content.style.maxHeight = overflowing && !this.expanded ? `${this.limit}px` : '';
      this.button.setAttribute('aria-expanded', String(this.expanded)); this.button.textContent = this.expanded ? this.button.dataset.less : this.button.dataset.more;
    }
    disconnectedCallback() { super.disconnectedCallback(); this.resize?.disconnect(); }
  });
  define('studio-testimonial', class extends ContentElement {
    connectedCallback() {
      if (!this.start()) return; this.button = this.querySelector('.st-testimonial-toggle'); if (!this.button) return;
      this.setAttribute('data-enhanced', ''); this.listen(this.button, 'click', () => this.setOpen(!this.hasAttribute('data-open')));
      this.listen(this, 'pointerenter', () => { if (this.dataset.trigger === 'hover') this.setOpen(true); });
      this.listen(this, 'pointerleave', () => { if (this.dataset.trigger === 'hover' && !this.contains(document.activeElement)) this.setOpen(false); });
      this.listen(this, 'focusin', (event) => { if (event.target !== this.button) this.setOpen(true); });
      this.listen(this, 'keydown', (event) => { if (event.key === 'Escape') { this.setOpen(false); this.button.focus(); } });
    }
    setOpen(open) { this.toggleAttribute('data-open', open); this.button.setAttribute('aria-expanded', String(open)); }
  });
  define('studio-content-motion', class extends ContentElement {
    connectedCallback() {
      if (!this.start()) return; this.pendingFrame = 0; this.visible = false;
      this.querySelectorAll('[data-reveal-pending]').forEach(node => node.removeAttribute('data-reveal-pending'));
      this.listen(this.querySelector('[data-st-motion-pause]'), 'click', (event) => { const paused = !this.hasAttribute('data-motion-paused'); this.toggleAttribute('data-motion-paused', paused); event.currentTarget.setAttribute('aria-pressed', String(paused)); });
      this.effects = [...this.querySelectorAll('.st-content-hero > .st-content-media, .st-feature-background > .st-content-media')];
      if (!this.effects.length && this.dataset.parallax === 'true') this.effects = [...this.querySelectorAll(':scope > div > .st-content-media')];
      const reduced = () => {
        this.style.removeProperty('--st-parallax-x'); this.style.removeProperty('--st-parallax-y'); this.style.removeProperty('--st-media-zoom');
        this.querySelectorAll('[data-reveal-pending]').forEach(node => node.removeAttribute('data-reveal-pending'));
      };
      const refresh = () => {
        this.pendingFrame = 0; if (!this.visible || motion.matches) return;
        const viewport = window.innerHeight || document.documentElement.clientHeight;
        const bounds = this.getBoundingClientRect(); const phase = Math.max(-1, Math.min(1, (viewport / 2 - bounds.top - bounds.height / 2) / ((viewport + bounds.height) / 2)));
        const direction = this.dataset.direction;
        if (this.dataset.parallax === 'true') {
          this.style.setProperty('--st-parallax-x', direction === 'horizontal' ? `${phase * 6}%` : '0%');
          this.style.setProperty('--st-parallax-y', direction === 'vertical' ? `${phase * 6}%` : '0%');
        }
        if (this.dataset.zoom === 'true' || direction === 'zoom') this.style.setProperty('--st-media-zoom', String(1.15 - Math.abs(phase) * .1));
      };
      const request = () => { if (!this.pendingFrame && this.visible && !motion.matches) this.pendingFrame = requestAnimationFrame(refresh); };
      if (this.dataset.parallax === 'true' || this.dataset.zoom === 'true') {
        this.effects.forEach(node => node.classList.add('st-motion-media'));
        this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; request(); }); this.observer.observe(this);
        this.listen(window, 'scroll', request, { passive: true }); this.listen(window, 'resize', request, { passive: true });
      }
      if (this.dataset.reveal === 'true' && !motion.matches && 'IntersectionObserver' in window) {
        this.reveals = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) { entry.target.removeAttribute('data-reveal-pending'); this.reveals.unobserve(entry.target); } }), { threshold: .04 });
        this.querySelectorAll('.st-content-card, .st-section-heading, .st-layered-scene').forEach(node => {
          const bounds = node.getBoundingClientRect();
          if (bounds.top >= window.innerHeight && bounds.height > 0) { node.classList.add('st-content-reveal'); node.setAttribute('data-reveal-pending', ''); this.reveals.observe(node); }
        });
      }
      this.listen(motion, 'change', () => { if (motion.matches) reduced(); else request(); });
      this.listen(document, 'shopify:block:select', (event) => event.target.closest('[data-reveal-pending]')?.removeAttribute('data-reveal-pending'));
    }
    disconnectedCallback() { super.disconnectedCallback(); cancelAnimationFrame(this.pendingFrame); this.observer?.disconnect(); this.reveals?.disconnect(); }
  });
  define('studio-content-text', class extends ContentElement {
    connectedCallback() {
      if (!this.start()) return; this.content = this.querySelector('[data-st-text]'); this.button = this.querySelector('.st-text-toggle'); if (!this.content || !this.button) return;
      this.lines = Math.max(1, Number(this.dataset.lines) || 3); this.expanded = false;
      this.style.setProperty('--st-text-lines', this.lines); this.setAttribute('data-collapsed', '');
      this.listen(this.button, 'click', () => { this.expanded = !this.expanded; this.toggleAttribute('data-collapsed', !this.expanded); this.button.setAttribute('aria-expanded', String(this.expanded)); this.button.textContent = this.expanded ? this.button.dataset.less : this.button.dataset.more; });
      this.listen(this.content, 'focusin', (event) => { if (!this.expanded && event.target.getBoundingClientRect().bottom > this.content.getBoundingClientRect().bottom) { this.expanded = true; this.removeAttribute('data-collapsed'); this.button.setAttribute('aria-expanded', 'true'); this.button.textContent = this.button.dataset.less; this.refresh(); } });
      this.resize = new ResizeObserver(() => this.refresh()); this.resize.observe(this.content); this.refresh();
    }
    refresh() { this.button.hidden = !this.expanded && this.content.scrollHeight <= this.content.clientHeight + 2; }
    disconnectedCallback() { super.disconnectedCallback(); this.resize?.disconnect(); }
  });
})();

(() => {
  if (customElements.get('mimsy-review-carousel')) return;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  customElements.define('mimsy-review-carousel', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      this.track = this.querySelector('[data-st-viewport]');
      if (!this.track) return;
      this.originals = Array.from(this.track.children).filter(card => !card.hasAttribute('data-review-clone'));
      this.previous = this.querySelector('[data-st-prev]');
      this.next = this.querySelector('[data-st-next]');
      this.counter = this.querySelector('[data-st-count]');
      this.progress = this.querySelector('.st-slider-progress');
      const on = (node, event, fn, options = {}) => node?.addEventListener(event, fn, { ...options, signal: this.events.signal });
      on(this.previous, 'click', () => this.move(-1));
      on(this.next, 'click', () => this.move(1));
      on(this.track, 'scroll', () => this.handleScroll(), { passive: true });
      on(this.track, 'scrollend', () => this.recenter());
      on(this.track, 'load', () => this.updateControls(), { capture: true });
      on(this.track, 'error', () => this.updateControls(), { capture: true });
      on(this.track, 'pointerdown', () => { this.dragging = true; clearTimeout(this.settleTimer); }, { passive: true });
      on(window, 'pointerup', () => { this.dragging = false; this.scheduleRecenter(); }, { passive: true });
      on(window, 'pointercancel', () => { this.dragging = false; this.scheduleRecenter(); }, { passive: true });
      on(this.track, 'keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.move((event.key === 'ArrowRight' ? 1 : -1) * (this.rtl ? -1 : 1));
      });
      on(document, 'shopify:block:select', event => {
        const card = this.originals.find(item => item === event.target || item.contains(event.target));
        card?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      });
      this.resize?.disconnect();
      this.resize = new ResizeObserver(() => {
        if (this.layoutFrame) return;
        this.layoutFrame = requestAnimationFrame(() => {
          this.layoutFrame = null;
          this.layoutLoop();
        });
      });
      this.layoutLoop();
      this.setAttribute('data-ready', '');
    }
    scrollPosition() {
      return this.track.scrollLeft * (this.rtl ? -1 : 1);
    }
    jumpTo(position) {
      cancelAnimationFrame(this.snapFrame);
      this.track.classList.add('mimsy-reviews-repositioning');
      this.track.scrollTo({ left: position * (this.rtl ? -1 : 1), behavior: 'instant' });
      this.updateControls();
      this.snapFrame = requestAnimationFrame(() => this.track?.classList.remove('mimsy-reviews-repositioning'));
    }
    layoutLoop() {
      if (!this.track || !this.originals.length) return;
      const style = getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
      const width = this.originals.reduce((total, card) => total + card.getBoundingClientRect().width, 0);
      const signature = `${this.track.clientWidth}:${width}:${gap}:${style.direction}`;
      if (signature === this.layoutSignature) {
        this.updateControls();
        return;
      }
      const phase = this.infinite && this.cycleWidth ? ((this.scrollPosition() % this.cycleWidth) + this.cycleWidth) % this.cycleWidth / this.cycleWidth : 0;
      this.layoutSignature = signature;
      clearTimeout(this.settleTimer);
      this.resize.disconnect();
      this.track.querySelectorAll('[data-review-clone]').forEach(card => card.remove());
      this.rtl = style.direction === 'rtl';
      this.cycleWidth = width + gap * this.originals.length;
      const interactive = this.originals.some(card => card.querySelector('a,button,input,select,textarea,details,iframe,video,[contenteditable]'));
      this.infinite = !interactive && this.originals.length > 1 && width + gap * (this.originals.length - 1) > this.track.clientWidth + 2;
      this.track.toggleAttribute('data-review-infinite', this.infinite);
      if (this.infinite) {
        const copy = () => {
          const fragment = document.createDocumentFragment();
          this.originals.forEach((card, index) => {
            const clone = card.cloneNode(true);
            clone.dataset.reviewClone = String(index);
            clone.setAttribute('aria-hidden', 'true');
            clone.setAttribute('inert', '');
            clone.removeAttribute('data-shopify-editor-block');
            clone.removeAttribute('id');
            clone.querySelectorAll('[id],[aria-labelledby],[aria-describedby],[aria-controls]').forEach(node => {
              ['id', 'aria-labelledby', 'aria-describedby', 'aria-controls'].forEach(attribute => node.removeAttribute(attribute));
            });
            fragment.append(clone);
          });
          return fragment;
        };
        this.track.prepend(copy());
        this.track.append(copy());
        this.jumpTo(this.cycleWidth * (1 + phase));
      } else this.jumpTo(0);
      this.resize.observe(this.track);
      Array.from(this.track.children).forEach(card => this.resize.observe(card));
      this.updateControls();
    }
    recenter() {
      if (!this.infinite || this.dragging || !this.cycleWidth) return;
      const position = this.scrollPosition();
      const target = this.cycleWidth + ((position % this.cycleWidth) + this.cycleWidth) % this.cycleWidth;
      if (Math.abs(position - target) > .5) this.jumpTo(target);
    }
    scheduleRecenter() {
      clearTimeout(this.settleTimer);
      if (this.infinite && !this.dragging) this.settleTimer = setTimeout(() => this.recenter(), 140);
    }
    handleScroll() {
      if (this.infinite) {
        const position = this.scrollPosition();
        const limit = this.track.scrollWidth - this.track.clientWidth;
        const buffer = Math.min(this.cycleWidth / 4, this.track.clientWidth / 2);
        if (position < buffer || position > limit - buffer) {
          const wasDragging = this.dragging;
          this.dragging = false;
          this.recenter();
          this.dragging = wasDragging;
        }
        this.scheduleRecenter();
      }
      this.updateControls();
    }
    move(step) {
      if (!this.originals?.length) return;
      this.recenter();
      const style = getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
      this.track.scrollBy({ left: (this.originals[0].getBoundingClientRect().width + gap) * step * (this.rtl ? -1 : 1), behavior: motion.matches ? 'instant' : 'smooth' });
    }
    updateControls() {
      if (!this.originals.length) return;
      const bounds = this.track.getBoundingClientRect();
      const start = this.rtl ? bounds.right : bounds.left;
      let closest = Infinity, index = 0;
      Array.from(this.track.children).forEach(card => {
        const box = card.getBoundingClientRect();
        const distance = Math.abs((this.rtl ? box.right : box.left) - start);
        if (distance < closest) {
          closest = distance;
          index = card.hasAttribute('data-review-clone') ? Number(card.dataset.reviewClone) : this.originals.indexOf(card);
        }
      });
      const overflowing = this.track.scrollWidth > this.track.clientWidth + 2;
      const first = this.originals[0].getBoundingClientRect();
      const last = this.originals.at(-1).getBoundingClientRect();
      if (this.previous) this.previous.disabled = !this.infinite && (!overflowing || (this.rtl ? first.right <= bounds.right + 3 : first.left >= bounds.left - 3));
      if (this.next) this.next.disabled = !this.infinite && (!overflowing || (this.rtl ? last.left >= bounds.left - 3 : last.right <= bounds.right + 3));
      if (this.counter) this.counter.textContent = overflowing ? `${index + 1} / ${this.originals.length}` : '';
      if (this.progress) {
        const value = overflowing ? Math.round((index + 1) / this.originals.length * 100) : 100;
        this.progress.setAttribute('aria-valuenow', String(value));
        this.progress.style.setProperty('--st-progress', `${value}%`);
      }
    }
    disconnectedCallback() {
      this.events?.abort();
      this.resize?.disconnect();
      clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.snapFrame);
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = null;
      this.track?.querySelectorAll('[data-review-clone]').forEach(card => card.remove());
      this.track?.removeAttribute('data-review-infinite');
      this.track?.classList.remove('mimsy-reviews-repositioning');
      this.removeAttribute('data-ready');
      this.layoutSignature = null;
      this.infinite = false;
      this.dragging = false;
    }
  });
})();

